"use client";

import { motion, AnimatePresence } from "framer-motion";
import { HiChevronDown, HiArrowRight } from "react-icons/hi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectFade } from "swiper/modules";
import { useHero } from "@/hooks/use-hero";
import { cn } from "@/lib/utils";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

export default function Hero() {
  const { activeSlide, handleScrollDown, handleSlideChange, slides } =
    useHero();

  return (
    <section className="relative h-screen w-full bg-black overflow-hidden">
      <Swiper
        modules={[Autoplay, Pagination, EffectFade]}
        effect="fade"
        speed={1500}
        loop={true}
        autoplay={{ delay: 6000, disableOnInteraction: false }}
        pagination={{
          clickable: true,
          el: ".custom-pagination",
        }}
        onSlideChange={handleSlideChange}
        className="h-full w-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div className="relative h-full w-full bg-black">
              {/* Video Background */}
              <video
                key={slide.id}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src={slide.src} type="video/webm" />
                Your browser does not support the video tag.
              </video>

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/30" />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white z-10 px-4">
                <AnimatePresence mode="wait">
                  {activeSlide === index && (
                    <motion.div
                      key={`content-${slide.id}`}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={{
                        hidden: { opacity: 0 },
                        visible: {
                          opacity: 1,
                          transition: { staggerChildren: 0.2 },
                        },
                      }}
                    >
                      <motion.h1
                        variants={{
                          hidden: { opacity: 0, y: 30 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 1, ease: "easeOut" },
                          },
                        }}
                        className="font-serif text-4xl md:text-6xl lg:text-7xl mb-4 tracking-normal drop-shadow-lg"
                      >
                        {slide.title}
                      </motion.h1>

                      <motion.p
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 1, delay: 0.3 },
                          },
                        }}
                        className="text-sm md:text-lg font-light tracking-[0.2em] uppercase max-w-2xl mx-auto drop-shadow-md text-white/90 mb-8"
                      >
                        {slide.subtitle}
                      </motion.p>

                      {/* CTA Button */}
                      {slide.cta && (
                        <motion.div
                          variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: {
                              opacity: 1,
                              y: 0,
                              transition: { duration: 0.8, delay: 0.6 },
                            },
                          }}
                        >
                          <a
                            href={slide.cta.link}
                            className="group inline-flex items-center gap-3 border border-white/50 hover:border-white text-white font-light px-8 py-3 md:px-10 md:py-3.5 text-sm md:text-base tracking-widest uppercase transition-all duration-500 hover:bg-white/5 backdrop-blur-sm"
                          >
                            <span>{slide.cta.text}</span>
                            <HiArrowRight
                              size={18}
                              className="group-hover:translate-x-1 transition-transform duration-300"
                            />
                          </a>
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination */}
      <div
        className={cn(
          "absolute z-20",
          "bottom-32 left-1/2 -translate-x-1/2",
          "md:bottom-auto md:left-12 md:top-1/2 md:-translate-y-1/2 md:translate-x-0",
        )}
      >
        <div
          className={cn(
            "custom-pagination flex items-center justify-center gap-4",
            "flex-row",
            "md:flex-col",
          )}
        ></div>
      </div>

      {/* Scroll Arrow */}
      <motion.button
        onClick={handleScrollDown}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 text-white/80 hover:text-white cursor-pointer p-4"
        aria-label="Scroll Down"
      >
        <motion.div
          initial={{ y: 0 }}
          animate={{
            y: [0, 12, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop",
            times: [0, 0.5, 1],
          }}
        >
          <HiChevronDown size={40} />
        </motion.div>
      </motion.button>

      <style jsx global>{`
        .custom-pagination .swiper-pagination-bullet {
          width: 5px;
          height: 5px;
          background: rgba(255, 255, 255, 0.4);
          opacity: 1;
          display: block;
          transition: all 0.3s ease;
        }
        .custom-pagination .swiper-pagination-bullet-active {
          background: #fff;
          transform: scale(1.2);
          position: relative;
        }
        .custom-pagination .swiper-pagination-bullet-active::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 16px;
          height: 16px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 50%;
        }

        @media (min-width: 768px) {
          .custom-pagination .swiper-pagination-bullet {
            width: 8px;
            height: 8px;
            margin: 12px 0 !important;
          }
          .custom-pagination .swiper-pagination-bullet-active {
            transform: scale(1.5);
          }
          .custom-pagination .swiper-pagination-bullet-active::after {
            width: 24px;
            height: 24px;
          }
        }
      `}</style>
    </section>
  );
}
